import {isTask} from '@domain/shifthandover'
import {OverdueLabel} from '@hconnect/common/components/eventProperties/OverdueLabel'
import {FIX_COLUMN_WIDTH} from '@hconnect/common/consts'
import {ShiftEvent, Task} from '@hconnect/common/types'
import {formatDateTime} from '@hconnect/common/utils/datetime.utils'
import {customThemeConstants, mergeSx} from '@hconnect/uikit/src/lib2'
import type {Column} from '@hconnect/uikit/src/lib2'
import {SxProps, Theme, Typography} from '@mui/material'
import {SystemStyleObject} from '@mui/system/styleFunctionSx/styleFunctionSx'
import {EquipmentLabel, EventStatusLabel} from '@widgets/common-shift-event'
import {TFunction} from 'i18next'
import {isEmpty} from 'lodash'
import {Moment} from 'moment-timezone'
import React from 'react'

// Types
export type TableSize = 'XL' | 'L' | 'M' | 'S' | 'XS'

export type ColumnsMap<T> = {[key in TableSize]: Column<T>[]}

// Helper components
type SecondaryProps = {
  sx?: SxProps<Theme>
  children: React.ReactNode | string
}

const Secondary: React.FC<SecondaryProps> = ({children, sx}) => {
  return (
    <Typography variant="body1" sx={mergeSx({fontWeight: 500}, sx)}>
      {children}
    </Typography>
  )
}

type LabelProps = {
  sx?: SxProps<Theme>
  children: React.ReactNode | string
}

const Label = ({children, sx}: LabelProps) => {
  return <Typography sx={mergeSx({fontSize: 12, lineHeight: '16px'}, sx)}>{children}</Typography>
}

// Column keys enum
export enum ColumnKeys {
  EventType = 'eventType',
  Title = 'title',
  EquipmentNumber = 'equipmentNumber',
  MainEquipmentNumber = 'mainEquipmentNumber',
  Category = 'category',
  Created = 'created',
  MaintenanceNotification = 'maintenanceNotification',
  Status = 'status'
}

// Column generators
type GenerateTitleArguments = {
  translation: TFunction
  type?: 'withEquipment' | 'regular'
  markOverdueTask?: boolean
  plantNow: Moment
}

export const generateTitleColumn = ({
  translation: t,
  type,
  markOverdueTask = true,
  plantNow
}: GenerateTitleArguments): Column<ShiftEvent> => {
  const title =
    type === 'withEquipment'
      ? `${t('shiftEvent.label.equipment')}: ${t('shiftEvent.label.title')}`
      : t('shiftEvent.label.title')

  return {
    key: ColumnKeys.Title,
    label: <Label>{title}</Label>,
    flex: 4,
    sortable: true,
    customTemplate: (item: ShiftEvent) => (
      <Typography
        variant="h4"
        color="textPrimary"
        sx={{fontSize: 16, fontWeight: 500, wordBreak: 'break-word'}}
      >
        {type === 'withEquipment' && item.equipment ? (
          <>
            <EquipmentLabel withId equipment={item.equipment} />: {item.title}
          </>
        ) : (
          <>
            {item.title}{' '}
            {markOverdueTask && isTask(item) && (
              <OverdueLabel status={item.status} dueDate={item.dueDate} plantNow={plantNow} />
            )}
          </>
        )}
      </Typography>
    )
  }
}

export const generateCategoryColumn = (t: TFunction) => ({
  key: ColumnKeys.Category,
  label: <Label>{t('shiftEvent.label.category')}</Label>,
  width: FIX_COLUMN_WIDTH,
  sortable: true,
  labelKey: 'shiftEvent.label.category',
  customTemplate: (item: ShiftEvent) => (
    <Secondary>
      {item.category
        ? t(`shiftEvent.category.${item.category}`)
        : t('shiftEvent.action.noneOfThat')}
    </Secondary>
  )
})

export const generateEquipmentNumberColumn = (t: TFunction): Column<ShiftEvent> => ({
  key: ColumnKeys.EquipmentNumber,
  label: <Label>{t('shiftEvent.label.equipment')}</Label>,
  flex: 2,
  minWidth: FIX_COLUMN_WIDTH,
  sortable: true,
  customTemplate: ({equipment}: ShiftEvent) => {
    return (
      <Secondary>
        {!isEmpty(equipment) ? (
          <EquipmentLabel withId withText withTooltip withEllipsis equipment={equipment} />
        ) : (
          ''
        )}
      </Secondary>
    )
  }
})

export const generateMainEquipmentNumberColumn = (t: TFunction): Column<ShiftEvent> => ({
  key: ColumnKeys.MainEquipmentNumber,
  label: <Label>{t('shiftEvent.label.mainEquipment')}</Label>,
  flex: 2,
  minWidth: FIX_COLUMN_WIDTH,
  sortable: true,
  customTemplate: ({mainEquipment}: ShiftEvent) => {
    return (
      <Secondary>
        {!isEmpty(mainEquipment) ? (
          <EquipmentLabel withId withText withTooltip withEllipsis equipment={mainEquipment} />
        ) : (
          ''
        )}
      </Secondary>
    )
  }
})

export const generateStatusColumn = (
  t: TFunction,
  type?: 'small' | 'regular'
): Column<ShiftEvent> => ({
  headProps: type === 'small' ? {style: {paddingLeft: '4px'}} : {},
  key: ColumnKeys.Status,
  width: type === 'small' ? '0px' : undefined,
  label: <Label>{t('shiftEvent.label.status')}</Label>,
  sortable: true,
  customTemplate: (item: ShiftEvent) => (
    <Secondary>
      <EventStatusLabel status={item.status} iconOnly={type === 'small'} />
    </Secondary>
  )
})

export const generateDueDateColumn = (t: TFunction): Column<ShiftEvent> => ({
  key: 'dueDate',
  label: <Label>{t('shiftEvent.label.dueDate')}</Label>,
  flex: 2,
  sortable: true,
  customTemplate: (item: ShiftEvent) => (
    <Secondary sx={{whiteSpace: 'nowrap'}}>{formatDateTime((item as Task).dueDate)}</Secondary>
  )
})

// Style utilities
const eventRowSx = (selected?: boolean) => ({
  cursor: 'pointer',
  backgroundColor: selected ? customThemeConstants().palette.contentSelected : 'none',
  '&:hover': {
    backgroundColor: selected
      ? customThemeConstants().palette.contentSelectedHover
      : customThemeConstants().palette.contentHover
  }
})

const getHighlightedEventSx = (selected?: boolean) => ({
  backgroundColor: selected ? 'rgba(218, 9, 1, 0.12)' : 'rgba(218, 9, 1, 0.04)',
  borderLeftStyle: 'solid',
  borderLeftWidth: 4,
  borderLeftColor: 'error.light',
  '&:hover': {
    backgroundColor: 'rgba(218, 9, 1, 0.08)'
  }
})

const getHighlightedEventCellSx = () => ({
  '& .MuiTableCell-body:first-of-type:before': {
    content: "''",
    width: 4,
    height: '100%',
    backgroundColor: 'error.light',
    position: 'absolute',
    left: 0,
    top: 0
  },
  '& .MuiTableCell-body': {
    position: 'relative'
  }
})

const markEvent: SystemStyleObject<Theme> = {
  backgroundColor: 'grey.100'
}

export const rowSx = (item: ShiftEvent, selected: boolean): SxProps<Theme> => {
  const isHighlighted = Boolean(item.isHighlighted)
  const isNotApproved = ['waitingForApproval', 'rejected'].includes(item.status)

  return [
    eventRowSx(selected),
    isHighlighted &&
      !isNotApproved && {...getHighlightedEventSx(selected), ...getHighlightedEventCellSx()},
    isNotApproved && markEvent
  ]
}
